import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
    Plus,
    School,
    Users,
    AlertTriangle,
    Edit2,
    Trash2,
    Mail,
    Phone
} from 'lucide-react';
import toast from 'react-hot-toast';

import {supabase} from '@/services/supabase';
import {
    Button,
    Input,
    Select,
    Table,
    Modal,
    LoadingSpinner,
    Badge,
    FilterBar
} from '@/components/ui';
import {US_STATES} from '@/constants';
import {buildAccountFilters} from '@/config/listFilters';
import {sortRows} from '@/utils/sorting';
import type {
    Account,
    Organization,
    CreateAccountData,
    UpdateAccountData,
    AccountFilters,
} from '@/types';
import {formatDate, isExpired} from "@/utils/formatters";
import {useAccountType} from '@/context/AccountTypeContext';

interface FormData {
    organization_id: string;
    name: string;
    contact_person_name: string;
    contact_person_email: string;
    contact_person_phone: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    zip_code: string;
    active_activation_date: string;
    account_expiration_date: string;
}

const initialFormData: FormData = {
    organization_id: '',
    name: '',
    contact_person_name: '',
    contact_person_email: '',
    contact_person_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    active_activation_date: '',
    account_expiration_date: ''
};

// Extended Account type with organization data
interface AccountWithOrganization extends Account {
    organizations: {
        name: string;
        state: string;
    };
}

export const ManageAccounts: React.FC = () => {
    const queryClient = useQueryClient();
    const {labels, accountType} = useAccountType();

    // State management
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingAccount, setEditingAccount] = useState<Account | null>(null);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filters, setFilters] = useState<AccountFilters>({
        account_type: accountType
    });
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Update filters when account type changes
    React.useEffect(() => {
        setFilters(prev => ({...prev, account_type: accountType}));
    }, [accountType]);

    // Fetch organizations for dropdown
    const {data: organizations} = useQuery({
        queryKey: ['organizations', accountType],
        queryFn: async (): Promise<Organization[]> => {
            const {data, error} = await supabase
                .from('organizations')
                .select('*')
                .eq('account_type', accountType)
                .order('name');

            if (error) throw error;
            return data || [];
        },
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: accounts,
        isLoading,
    } = useQuery({
        queryKey: ['accounts', filters, searchTerm, sortBy, sortOrder],
        queryFn: async (): Promise<AccountWithOrganization[]> => {
            let query = supabase
                .from('accounts')
                .select('*, organizations!inner(name, state)')
                .eq('account_type', accountType);

            if (searchTerm) {
                query = query.or(`name.ilike.%${searchTerm}%,contact_person_email.ilike.%${searchTerm}%`);
            }

            if (filters.organization_id) {
                query = query.eq('organization_id', filters.organization_id);
            }

            if (filters.state) {
                query = query.eq('state', filters.state);
            }

            if (filters.status) {
                query = query.eq('status', filters.status);
            }

            // Direct columns sort server-side; the District column is a joined
            // organization name, so sort it client-side after fetching.
            if (sortBy !== 'organization') {
                query = query.order(sortBy, {ascending: sortOrder === 'asc'});
            }

            const {data, error} = await query;
            if (error) throw error;
            const rows = data || [];
            return sortBy === 'organization'
                ? sortRows(rows, sortOrder, (r) => r.organizations?.name || '')
                : rows;
        },
        staleTime: 30 * 1000,
    });

    // Create account mutation
    const createAccountMutation = useMutation({
        mutationFn: async (data: CreateAccountData): Promise<Account> => {
            const {data: result, error} = await supabase
                .from('accounts')
                .insert(data)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            toast.success(`${labels.accountSingular} created successfully`);
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(`Failed to create ${labels.accountSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Update account mutation
    const updateAccountMutation = useMutation({
        mutationFn: async ({id, data}: { id: string; data: UpdateAccountData }): Promise<Account> => {
            const {data: result, error} = await supabase
                .from('accounts')
                .update(data)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            toast.success(`${labels.accountSingular} updated successfully`);
            handleCloseModal();
        },
        onError: (error: any) => {
            toast.error(`Failed to update ${labels.accountSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Delete account mutation
    const deleteAccountMutation = useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const {error} = await supabase
                .from('accounts')
                .delete()
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['accounts']});
            toast.success(`${labels.accountSingular} deleted successfully`);
        },
        onError: (error: any) => {
            toast.error(`Failed to delete ${labels.accountSingular.toLowerCase()}: ${error.message}`);
        },
    });

    // Helper functions
    const filteredCount = accounts?.length || 0;
    const expiredCount = accounts?.filter(acc =>
        acc.account_expiration_date && isExpired(acc.account_expiration_date)
    ).length || 0;

    const organizationOptions = organizations?.map(org => ({
        value: org.id,
        label: org.name
    })) || [];

    const handleSort = (column: string, order: 'asc' | 'desc'): void => {
        setSortBy(column);
        setSortOrder(order);
    };

    const handleOpenModal = (account: Account | null = null): void => {
        if (account) {
            setEditingAccount(account);
            setFormData({
                organization_id: account.organization_id,
                name: account.name,
                contact_person_name: account.contact_person_name,
                contact_person_email: account.contact_person_email,
                contact_person_phone: account.contact_person_phone || '',
                address_line1: account.address_line1 || '',
                address_line2: account.address_line2 || '',
                city: account.city || '',
                state: account.state,
                zip_code: account.zip_code || '',
                active_activation_date: account.active_activation_date?.split('T')[0] || '',
                account_expiration_date: account.account_expiration_date?.split('T')[0] || ''
            });
        } else {
            setEditingAccount(null);
            setFormData(initialFormData);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsModalOpen(false);
        setEditingAccount(null);
        setFormData(initialFormData);
    };

    const handleInputChange = (field: keyof FormData, value: string): void => {
        // Normalize contact_person_email to lowercase
        const normalizedValue = field === 'contact_person_email'
            ? value.toLowerCase()
            : value;

        setFormData(prev => ({...prev, [field]: normalizedValue}));
    };

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        const submitData: CreateAccountData = {
            organization_id: formData.organization_id,
            name: formData.name.trim(),
            contact_person_name: formData.contact_person_name.trim(),
            contact_person_email: formData.contact_person_email.trim(),
            contact_person_phone: formData.contact_person_phone.trim() || undefined,
            address_line1: formData.address_line1.trim() || undefined,
            address_line2: formData.address_line2.trim() || undefined,
            city: formData.city.trim() || undefined,
            state: formData.state,
            zip_code: formData.zip_code.trim() || undefined,
            account_type: accountType,
            status: 'active',
            active_activation_date: formData.active_activation_date || undefined,
            account_expiration_date: formData.account_expiration_date || undefined
        };

        if (editingAccount) {
            updateAccountMutation.mutate({
                id: editingAccount.id,
                data: submitData
            });
        } else {
            createAccountMutation.mutate(submitData);
        }
    };

    const handleDelete = async (account: Account): Promise<void> => {
        if (window.confirm(`Are you sure you want to delete "${account.name}"?`)) {
            deleteAccountMutation.mutate(account.id);
        }
    };

    // Table columns configuration
    const columns = [
        {
            key: 'name',
            title: `${labels.accountSingular} Name`,
            sortable: true,
            render: (value: string, record: AccountWithOrganization) => (
                <div className="flex items-start space-x-3">
                    <School className="h-5 w-5 text-blue-500 mt-0.5"/>
                    <div>
                        <p className="font-medium text-gray-900">{value}</p>
                        <p className="text-sm text-gray-500">
                            {record.organizations?.name} • {record.organizations?.state}
                        </p>
                        {record.city && record.state && (
                            <p className="text-sm text-gray-400">
                                {record.city}, {record.state}
                            </p>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'organization',
            title: labels.organizationSingular,
            sortable: true,
            render: (_: any, record: AccountWithOrganization) => (
                <div>
                    <p className="font-medium text-gray-900">
                        {record.organizations?.name ?? '—'}
                    </p>
                    {record.organizations?.state && (
                        <p className="text-sm text-gray-500">
                            {record.organizations.state}
                        </p>
                    )}
                </div>
            ),
        },
        {
            key: 'contact_person_name',
            title: 'Contact Person',
            sortable: true,
            render: (value: string, record: Account) => (
                <div>
                    <p className="font-medium text-gray-900">{value}</p>
                    <div className="flex items-center space-x-1 mt-1">
                        <Mail className="h-3 w-3 text-gray-400"/>
                        <p className="text-sm text-gray-500">{record.contact_person_email}</p>
                    </div>
                    {record.contact_person_phone && (
                        <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3 text-gray-400"/>
                            <p className="text-sm text-gray-500">{record.contact_person_phone}</p>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            sortable: true,
            render: (value: string, record: Account) => {
                const expired = record.account_expiration_date && isExpired(record.account_expiration_date);
                return (
                    <div className="space-y-1">
                        <Badge variant={value === 'active' && !expired ? 'success' : 'default'}>
                            {expired ? 'Expired' : value}
                        </Badge>
                        {record.account_expiration_date && (
                            <p className="text-xs text-gray-500">
                                Expires: {formatDate(record.account_expiration_date)}
                            </p>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_: any, record: Account) => (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleOpenModal(record)}
                        className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                        title="Edit"
                    >
                        <Edit2 className="h-4 w-4"/>
                    </button>
                    <button
                        onClick={() => handleDelete(record)}
                        className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                        title="Delete"
                    >
                        <Trash2 className="h-4 w-4"/>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manage {labels.accountPlural}</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        {accountType === 'business'
                            ? `Create and manage ${labels.accountPlural.toLowerCase()} within ${labels.organizationPlural.toLowerCase()}`
                            : `Create and manage schools within districts`}
                    </p>
                </div>
                <Button onClick={() => handleOpenModal()} className="flex items-center space-x-2">
                    <Plus className="h-4 w-4"/>
                    <span>Add {labels.accountSingular}</span>
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total {labels.accountPlural}</p>
                            <p className="text-2xl font-bold text-gray-900">{filteredCount}</p>
                        </div>
                        <School className="h-8 w-8 text-blue-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active {labels.accountPlural}</p>
                            <p className="text-2xl font-bold text-green-600">{filteredCount - expiredCount}</p>
                        </div>
                        <Users className="h-8 w-8 text-green-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Expired</p>
                            <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500"/>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total {labels.hostPlural}</p>
                            <p className="text-2xl font-bold text-purple-600">0</p>
                        </div>
                        <Users className="h-8 w-8 text-purple-500"/>
                    </div>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <FilterBar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={`Search ${labels.accountPlural.toLowerCase()}...`}
                filters={buildAccountFilters({
                    labels,
                    filters,
                    onChange: (patch) => setFilters(prev => ({...prev, ...patch})),
                    organizationOptions,
                })}
            />

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <LoadingSpinner size="lg"/>
                    </div>
                ) : accounts && accounts.length > 0 ? (
                    <Table
                        columns={columns}
                        data={accounts}
                        loading={isLoading}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <School className="h-12 w-12 text-gray-400 mx-auto mb-4"/>
                            <p className="text-gray-500">No {labels.accountPlural.toLowerCase()} found</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingAccount ? `Edit ${labels.accountSingular}` : `Add ${labels.accountSingular}`}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {labels.organizationSingular} *
                                </label>
                                <Select
                                    value={formData.organization_id}
                                    onChange={(value) => handleInputChange('organization_id', value)}
                                    options={organizationOptions}
                                    placeholder={`Select ${labels.organizationSingular.toLowerCase()}`}
                                    required
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {labels.accountSingular} Name *
                                </label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder={`Enter ${labels.accountSingular.toLowerCase()} name`}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Contact Person Name *
                                </label>
                                <Input
                                    value={formData.contact_person_name}
                                    onChange={(e) => handleInputChange('contact_person_name', e.target.value)}
                                    placeholder="Enter contact name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Contact Person Email *
                                </label>
                                <Input
                                    type="email"
                                    value={formData.contact_person_email}
                                    onChange={(e) => handleInputChange('contact_person_email', e.target.value)}
                                    placeholder="Enter contact email"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Contact Phone
                                </label>
                                <Input
                                    type="tel"
                                    value={formData.contact_person_phone}
                                    onChange={(e) => handleInputChange('contact_person_phone', e.target.value)}
                                    placeholder="Enter phone number"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Address Information */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Address Line 1
                                </label>
                                <Input
                                    value={formData.address_line1}
                                    onChange={(e) => handleInputChange('address_line1', e.target.value)}
                                    placeholder="Enter street address"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Address Line 2
                                </label>
                                <Input
                                    value={formData.address_line2}
                                    onChange={(e) => handleInputChange('address_line2', e.target.value)}
                                    placeholder="Apartment, suite, etc."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    City
                                </label>
                                <Input
                                    value={formData.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    placeholder="Enter city"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    State *
                                </label>
                                <Select
                                    value={formData.state}
                                    onChange={(value) => handleInputChange('state', value)}
                                    options={US_STATES.map(state => ({value: state, label: state}))}
                                    placeholder="Select state"
                                    required
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ZIP Code
                                </label>
                                <Input
                                    value={formData.zip_code}
                                    onChange={(e) => handleInputChange('zip_code', e.target.value)}
                                    placeholder="Enter ZIP code"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Account Dates */}
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Active Activation Date
                                </label>
                                <Input
                                    type="date"
                                    value={formData.active_activation_date}
                                    onChange={(e) => handleInputChange('active_activation_date', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Account Expiration Date
                                </label>
                                <Input
                                    type="date"
                                    value={formData.account_expiration_date}
                                    onChange={(e) => handleInputChange('account_expiration_date', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCloseModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            isLoading={createAccountMutation.isPending || updateAccountMutation.isPending}
                        >
                            {editingAccount ? `Update ${labels.accountSingular}` : `Create ${labels.accountSingular}`}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};
